import { useState } from 'react';
import { Modal, Form, Select, Input, Button, Space, Tag, RadioGroup, Row, Col, message } from '../../ui';
import { Paperclip, X } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request } from '../../api/client';
import { useAuthStore, usePermissionChecker } from '../../store/auth';
import { FileUploadField } from '../../components/FileUploadField';

const CATEGORIES = [
  'Product Quality', 'Delivery Issue', 'Packaging Damage', 'Billing / Payment',
  'Wrong Product', 'Shortage / Missing', 'Service', 'Other',
];
const SEVERITIES = [
  { value: 'LOW', label: 'Low' },
  { value: 'MED', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
  { value: 'CRIT', label: 'Critical' },
];

export function ComplaintFormModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [form] = Form.useForm();
  const qc = useQueryClient();
  const [attachments, setAttachments] = useState<string[]>([]);
  const [partyType, setPartyType] = useState<'RETAILER' | 'FARMER'>('RETAILER');

  // Staff (admin / super admin / sales head / sales person) raise complaints ON
  // BEHALF OF a retailer or farmer; a customer login is auto-scoped to itself by
  // the backend, so they don't pick a party.
  const isStaff = !useAuthStore((s) => s.isCustomer)();
  const hasPerm = usePermissionChecker();

  const { data: retailers } = useQuery({
    queryKey: ['complaint-retailers'],
    queryFn: () => request<any>({ url: '/retailers', params: { pageSize: 200 } }),
    enabled: open && isStaff && hasPerm('RETAILER.VIEW'),
  });
  const { data: farmers } = useQuery({
    queryKey: ['complaint-farmers'],
    queryFn: () => request<any>({ url: '/farmers', params: { pageSize: 200 } }),
    enabled: open && isStaff && hasPerm('FARMER.VIEW'),
  });
  const { data: products } = useQuery({
    // Active products only — archived/deactivated SKUs must not be selectable as a complaint subject.
    queryKey: ['complaint-products', 'active'],
    queryFn: () => request<any>({ url: '/products', params: { pageSize: 200, active: 'true' } }),
    enabled: open,
  });

  const create = useMutation({
    mutationFn: (vals: any) => request<any>({
      url: '/complaints',
      method: 'POST',
      data: {
        category: vals.category,
        severity: vals.severity,
        description: vals.description,
        productId: vals.productId || undefined,
        batchNo: vals.batchNo || undefined,
        attachments: attachments.length ? attachments : undefined,
        source: 'WEB',
        // On-behalf-of: only staff send a party; customers are scoped server-side.
        ...(isStaff && partyType === 'RETAILER' ? { retailerId: vals.partyId } : {}),
        ...(isStaff && partyType === 'FARMER' ? { farmerId: vals.partyId } : {}),
      },
    }),
    onSuccess: (c: any) => {
      message.success(`Complaint ${c?.complaintNo ?? ''} raised`);
      qc.invalidateQueries({ queryKey: ['complaints'] });
      form.resetFields();
      setAttachments([]);
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Could not raise complaint'),
  });

  const close = () => { form.resetFields(); setAttachments([]); onClose(); };

  return (
    <Modal open={open} onOpenChange={(o) => { if (!o) close(); }} title="Raise a Complaint" width={720}>
      <Form form={form} layout="vertical" onFinish={(v) => create.mutate(v)} initialValues={{ severity: 'MED' }}>
        {isStaff && (
          <Row gutter={16}>
            <Col xs={24} sm={10}>
              <Form.Item label="Raise on behalf of">
                <RadioGroup
                  inline
                  value={partyType}
                  onChange={(v) => { setPartyType(v as 'RETAILER' | 'FARMER'); form.setFieldValue('partyId', undefined); }}
                  options={[{ value: 'RETAILER', label: 'Retailer' }, { value: 'FARMER', label: 'Farmer' }]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={14}>
              <Form.Item
                name="partyId"
                label={partyType === 'RETAILER' ? 'Retailer' : 'Farmer'}
                rules={[{ required: true, message: `Select the ${partyType === 'RETAILER' ? 'retailer' : 'farmer'} this is for` }]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder={partyType === 'RETAILER' ? 'Search retailers…' : 'Search farmers…'}
                  options={
                    partyType === 'RETAILER'
                      ? (retailers?.data ?? []).map((r: any) => ({ value: String(r.id), label: `${r.companyName} (${r.code})` }))
                      : (farmers?.data ?? []).map((f: any) => ({ value: String(f.id), label: `${f.fullName}${f.mobile ? ` · ${f.mobile}` : ''}` }))
                  }
                />
              </Form.Item>
            </Col>
          </Row>
        )}

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item name="category" label="Category" rules={[{ required: true, message: 'Select a category' }]}>
              <Select options={CATEGORIES.map((c) => ({ value: c, label: c }))} placeholder="What is this about?" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="severity" label="How urgent is it?" rules={[{ required: true }]}>
              <Select options={SEVERITIES} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="description" label="Describe the issue" rules={[{ required: true, message: 'Please describe the issue' }]}>
          <Input.TextArea rows={4} placeholder="Tell us what happened…" />
        </Form.Item>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item name="productId" label="Related product (optional)">
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Pick a product"
                options={(products?.data ?? []).map((p: any) => ({ value: String(p.id), label: `${p.name} · ${p.sku}` }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="batchNo" label="Batch number (optional)">
              <Input placeholder="If printed on the pack" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label="Photos / attachments (optional)">
          {attachments.length > 0 && (
            <Space size={6} wrap style={{ marginBottom: 8 }}>
              {attachments.map((url) => (
                <Tag key={url}>
                  <Paperclip size={16} />
                  {url.split('/').pop()}
                  <button
                    type="button"
                    aria-label="Remove attachment"
                    className="ml-0.5 inline-flex text-muted hover:text-content"
                    onClick={() => setAttachments((a) => a.filter((u) => u !== url))}
                  >
                    <X size={16} />
                  </button>
                </Tag>
              ))}
            </Space>
          )}
          <FileUploadField
            category="attachment"
            buttonLabel="Attach file"
            preview={false}
            allowUrlPaste={false}
            value=""
            onChange={(url) => { if (url) setAttachments((a) => [...a, url]); }}
          />
        </Form.Item>

        <Button variant="primary" type="submit" block loading={create.isPending}>
          Submit complaint
        </Button>
      </Form>
    </Modal>
  );
}
